import { stringify as qsStringify } from 'qs';
import { filterEmptyValues } from './util-functions';
import { CreateEventTicketDto } from '../models/dto';
import { sub } from 'date-fns';

export const changeLocation = ({
	url,
	fallback,
	timeout = 2000,
	openFallback = true,
}: {
	url: string;
	fallback?: string;
	timeout?: number;
	openFallback?: boolean;
}) => {
	if (typeof window === 'undefined' || !url) {
		return;
	}

	console.log('deeplink: location.href = ', url);
	console.log('fallback: ', fallback);

	window.location.href = url;

	if (openFallback && fallback && fallback !== url) {
		setTimeout(function () {
			window.location.href = fallback;
		}, timeout);
	}
};

export const windowOpen = ({
	url,
	fallback,
	target = '_blank',
	timeout = 2000,
	openFallback = true,
}: {
	url: string;
	fallback?: string;
	target?: '_self' | '_blank' | '_parent' | '_top';
	timeout?: number;
	openFallback?: boolean;
}) => {
	if (typeof window === 'undefined' || !url) {
		return;
	}

	console.log('deeplink: window.open = ', url);
	console.log('fallback: ', fallback);

	const newWindow = window.open(url, target);

	if (!newWindow && openFallback && fallback && fallback !== url) {
		setTimeout(function () {
			window.location.href = fallback;
		}, timeout);
	}
};

export const clickPhantomAnchor = ({
	url,
	fallback,
	download,
	target,
	timeout = 2000,
	openFallback = true,
}: {
	url: string;
	fallback?: string;
	download?: string;
	target?: '_self' | '_blank' | '_parent' | '_top';
	timeout?: number;
	openFallback?: boolean;
}) => {
	if (typeof window === 'undefined' || !url) {
		return;
	}

	console.log('deeplink: anchor.click = ', url);
	console.log('fallback: ', fallback);

	const encodedUri = encodeURI(url);

	const wrapperDiv = document.createElement('div');
	wrapperDiv.style.display = 'none';

	const link = document.createElement('a');
	link.setAttribute('href', encodedUri);

	if (download) {
		link.setAttribute('download', download);
	}

	if (target) {
		link.setAttribute('target', target);
	}

	wrapperDiv.appendChild(link);
	document.body.appendChild(wrapperDiv);

	link.click();
	wrapperDiv.remove();

	if (fallback && openFallback && fallback !== url) {
		setTimeout(function () {
			window.location.href = fallback;
		}, timeout);
	}
};

export const createPhantomIFrame = ({
	url,
	fallback,
	timeout = 1000,
	openFallback = true,
}: {
	url: string;
	fallback?: string;
	timeout?: number;
	openFallback?: boolean;
}): void => {
	if (typeof window === 'undefined' || !url) {
		return;
	}

	console.log('deeplink: iframe.load = ', url);
	console.log('fallback: ', fallback);

	const iframe = document.createElement('iframe');
	iframe.style.border = 'none';
	iframe.style.width = '1px';
	iframe.style.height = '1px';
	iframe.src = url;

	iframe.onload = function () {
		console.log('iframe loaded');
		window.location.href = url;
	};

	iframe.onerror = function () {
		console.log('iframe error');

		if (!openFallback || !fallback) return;

		window.location.href = fallback;
	};

	document.body.appendChild(iframe);

	console.log('append iframe');

	setTimeout(function () {
		console.log('remove iframe');
		document.body.removeChild(iframe);

		if (!openFallback || !fallback) return;

		clickPhantomAnchor({
			url: fallback,
			target: '_blank',
		});
	}, timeout);
};

export const openEmailApp = (email: string, props?: {
	subject?: string;
	body?: string;
}) => {
	if (typeof window === 'undefined') {
		return;
	}

	if (!email) {
		throw new Error('Email is required');
	}

	const query = qsStringify({
		subject: props?.subject,
		body: props?.body,
	});

	window.location.href = `mailto:${email}${query ? `?${query}` : ''}`;
};

export const makeEventPageLink = ({
	eventAlias,
	shortLocalInstance,
	isCheckout,
	isWaitlist,
	preselectedTierId,
	preselectedTierQty,
	playwright,
	e2eToken,
	cardInfo,
	tickets,
}: {
	eventAlias: string;
	shortLocalInstance?: string;
	isCheckout?: boolean;
	isWaitlist?: boolean;
	preselectedTierId?: string;
	preselectedTierQty?: number;
	tickets?: CreateEventTicketDto[];
	playwright?: boolean;
	e2eToken?: string;
	cardInfo?: {
		CARD_NUMBER: string;
		CARD_EXP_MONTH: string;
		CARD_EXP_YEAR: string;
		CARD_CVC: string;
	};
}) => {
	const params = qsStringify(
		filterEmptyValues({
			checkout: isCheckout && !isWaitlist ? 'true' : undefined,
			waitlist: isWaitlist ? 'true' : undefined,
			preselectedTierId,
			preselectedTierQty,
			playwright: playwright ? 'true' : undefined,
			e2eToken,
			ticketsJson: tickets?.length ? JSON.stringify(tickets) : undefined,
			...(cardInfo ?? {}),
		})
	);

	return `event/${eventAlias}${shortLocalInstance ? `/${shortLocalInstance}` : ''}${params ? `?${params}` : ''}`;
};

export const makeHostPageLink = ({ userName }: { userName: string }) => `host/${userName}`;

export const makeChallengePageLink = ({ challengeAlias }: { challengeAlias: string }) => `challenge/${challengeAlias}`;
